import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../../components/Footer';
import { ArrowLeft, CheckCircle, MapPin, Phone } from 'lucide-react';

const locations = [
  'Scarsdale', 'Greenwich', 'Rye', 'Harrison', 'Mamaroneck', 'Bedford',
  'Chappaqua', 'Bronxville', 'Larchmont', 'Purchase'
];

const features = [
  {
    title: 'Drywall Repair',
    description: 'Expert repair of holes, cracks, and water damage.'
  },
  {
    title: 'Paint Touch-ups',
    description: 'Professional color matching and paint repairs.'
  },
  {
    title: 'Door Adjustments',
    description: 'Repair and adjustment of doors and hardware.'
  },
  {
    title: 'Window Repairs',
    description: 'Fix window operation and seal issues.'
  },
  {
    title: 'Hardware Updates',
    description: 'Replacement of handles, locks, and fixtures.'
  },
  {
    title: 'General Maintenance',
    description: 'Regular upkeep and preventive maintenance.'
  }
];

const portfolioImages = [
  {
    url: 'https://images.unsplash.com/photo-1581783898377-1c85bf937427?auto=format&fit=crop&q=80&w=1200',
    location: 'Scarsdale Home Repair'
  },
  {
    url: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80&w=1200',
    location: 'Greenwich Maintenance'
  },
  {
    url: 'https://images.unsplash.com/photo-1590479773265-7464e5d48118?auto=format&fit=crop&q=80&w=1200',
    location: 'Rye Home Updates'
  }
];

export function GeneralRepairsPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>General Home Repairs in Westchester County | Golden Touch</title>
        <meta name="description" content="Professional home repair and maintenance services in Westchester County & Greenwich. Quality workmanship for all your home repair needs." />
        <meta name="keywords" content="home repairs Westchester, maintenance services Greenwich, handyman Scarsdale, home fix-it contractor" />
        <link rel="canonical" href="https://renovationwestchester.com/services/general-repairs" />
      </Helmet>


      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Services</span>
          </Link>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                General Home Repairs in Westchester
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Keep your home in top condition with our professional repair and maintenance services. 
                From small fixes to ongoing maintenance, we provide quality workmanship for all your 
                home repair needs.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 mb-8">
                <MapPin className="h-5 w-5" />
                <span className="text-sm tracking-wider">Westchester County & Greenwich Area</span>
              </div>
              <Link 
                to="/consultation"
                className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider">Schedule a Consultation</span>
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={portfolioImages[0].url}
                alt="Home Repairs in Westchester"
                className="w-full h-full object-cover"
                width="1200"
                height="900"
              />
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Our Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-zinc-800/30 p-8">
                  <CheckCircle className="h-8 w-8 text-yellow-400 mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-4">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Areas Served</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {locations.map((location) => (
                <Link
                  key={location}
                  to={`/areas/${location.toLowerCase()}`}
                  className="block bg-zinc-800/30 p-4 text-center border border-transparent hover:border-yellow-400/40 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                  <span className="text-zinc-300">{location}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Need Something Fixed?</h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              From small repairs to regular maintenance, our team is ready to help keep your 
              home in perfect condition. Quality workmanship guaranteed.
            </p>
            <Link 
              to="/consultation"
              className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm tracking-wider">Schedule Your Consultation</span>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}